/**
 * Client-side validation for the account-edit form.
 *
 * Only covers the fields the legacy form actually rejected on the
 * client (required markers in BasicInfoSection + AddressForm). The
 * backend continues to run its own FluentValidation pass — server
 * errors still land in `accountEditingSlice.backendErrors` and are
 * mapped onto the same Formik field keys, so this schema and the
 * server validation complement rather than duplicate each other.
 *
 * Factory signature takes the i18n `forms` bag so error messages
 * stay localised without forcing this module to reach into Redux.
 */
import * as Yup from 'yup';

const addressShape = (forms = {}) =>
  Yup.object({
    streetAddress: Yup.string()
      .trim()
      .required(forms.requiredField || 'Required'),
    city: Yup.string()
      .trim()
      .required(forms.requiredField || 'Required'),
    state: Yup.string()
      .trim()
      .required(forms.requiredField || 'Required'),
    zipCode: Yup.string()
      .trim()
      .required(forms.requiredField || 'Required'),
    // `suite` is optional per legacy — leave untyped.
  });

/**
 * Highest fee a CPT service row may carry, on either fee field
 * (QA request 2026-09-30). The API has no ceiling of its own, so this
 * is the only thing standing between a slipped keystroke and a claim
 * raised for an absurd amount.
 */
export const MAX_CPT_PRICE = 5000;

const isBlank = value => value === undefined || value === null || String(value).trim() === '';

const isAbovePrice = (value, floor) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) && numeric > floor;
};

// A blank fee is the other rules' business: `price` is `.required()`
// and `initialPrice` is optional, so neither is failed here for being
// empty.
const isWithinMaxPrice = value => {
  if (isBlank(value)) return true;
  const numeric = Number(value);
  return Number.isFinite(numeric) && numeric <= MAX_CPT_PRICE;
};

export const buildAccountValidationSchema = (forms = {}) => {
  const required = forms.requiredField || 'Required';
  // Interpolated by hand rather than through `getTransitionWithVariable`:
  // that helper lives in `redux_store`, and importing it here would pull
  // MUI into a module that is deliberately nothing but Yup — which is
  // what keeps this schema testable on its own.
  const maxPriceMessage = (forms.priceAtMost || 'Price must be at most ${{max}}.').replace(
    '{{max}}',
    String(MAX_CPT_PRICE)
  );
  // A conservative phone check: digits, spaces, parens, dashes and a
  // leading + sign. The authoritative phone rule (country specific)
  // lives on the server — we just block obviously empty inputs here.
  const phonePattern = /^[+()0-9\s-]+$/;

  return Yup.object({
    customTags: Yup.array().of(Yup.string()).min(1, required).required(required),

    businessName: Yup.string().trim().required(required),
    tradeName: Yup.string().trim().required(required),

    phone: Yup.string()
      .trim()
      .required(required)
      .matches(phonePattern, {
        message: forms.invalidPhone || 'Invalid phone',
        excludeEmptyString: true,
      }),
    // Fax is optional — only format-checked when the user typed
    // something. Without `excludeEmptyString`, Yup's matches() flags
    // an empty string since the regex requires ≥1 char.
    fax: Yup.string()
      .trim()
      .matches(phonePattern, {
        message: forms.invalidPhone || 'Invalid phone',
        excludeEmptyString: true,
      })
      .nullable()
      .notRequired(),

    organizationAddress: addressShape(forms),
    // `shippingAddress` is only validated when `isShippingTheSame`
    // is false; Formik feeds both shapes through unconditionally, so
    // we use `when` to drop the rule when the toggle is on.
    isShippingTheSame: Yup.boolean(),
    shippingAddress: Yup.object().when('isShippingTheSame', {
      is: true,
      then: schema => schema.strip(),
      otherwise: () => addressShape(forms),
    }),

    // `otherAddresses` is a Formik FieldArray of address entries;
    // each entry must still validate the core address fields.
    otherAddresses: Yup.array().of(
      Yup.object({
        name: Yup.string().trim().required(required),
        streetAddress: Yup.string().trim().required(required),
        city: Yup.string().trim().required(required),
        state: Yup.string().trim().required(required),
        zipCode: Yup.string().trim().required(required),
      })
    ),

    // ISP services (CPT codes) — CptCodesSection lets a row be added
    // with a blank code and/or a blank price. Sending that to the
    // server produces a JSON Patch with `"value":""`, which the API
    // rejects, so it must be caught client-side before submit.
    ispConfiguration: Yup.object({
      services: Yup.array().of(
        Yup.object({
          cptCode: Yup.string().trim().required(required),
          price: Yup.string()
            .trim()
            .required(required)
            .test(
              'isPositivePrice',
              forms.priceGreaterThanZero || 'Price must be greater than $0.',
              // Blank values are already reported by `.required()` above
              // — this test only guards the "> 0" business rule so the
              // two don't both fire for the same empty field.
              value => isBlank(value) || isAbovePrice(value, 0)
            )
            .test('isWithinMaxPrice', maxPriceMessage, value => isWithinMaxPrice(value)),
          // `initialPrice` is a server-tracked override the UI never
          // requires a value for, so it is only checked when one is
          // given. It is capped alongside `price`: a ceiling on one fee
          // field and not the other is a ceiling anyone can step over.
          initialPrice: Yup.string()
            .trim()
            .test('isWithinMaxInitialPrice', maxPriceMessage, value => isWithinMaxPrice(value)),
        })
      ),
    }),
  });
};
